/**
 * Keep the API and SSR hosts the SAME as the host the page is served from.
 *
 * REACT_APP_API_BASE_URL is baked in at build time as a single literal
 * (http://localhost:8000). Open the app on http://127.0.0.1:3000 instead and
 * every request still goes to localhost:8000 — a DIFFERENT site to the browser,
 * because 127.0.0.1 and localhost are distinct hosts. The auth cookies are set
 * `SameSite=lax`, which withholds them on cross-site XHR, so /auth/status came
 * back {"authenticated": false} and the app redirected to /login-required even
 * though the login itself had returned 200. It reads as rejected credentials
 * and is not — the hostname is the whole problem.
 *
 * So when the page is on a loopback host, point these at that same host. Only
 * loopback is rewritten — a deployed origin keeps its configured value exactly
 * as-is — and only the hostname changes, never the scheme or port.
 */
const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

const sameHostAsPage = (configured) => {
  if (typeof window === "undefined" || !configured) return configured;

  try {
    const url = new URL(configured);
    const pageHost = window.location.hostname;

    // Both ends must be loopback before we touch anything.
    if (!LOOPBACK.has(url.hostname) || !LOOPBACK.has(pageHost)) return configured;
    if (url.hostname === pageHost) return configured;

    url.hostname = pageHost;
    return url.origin;
  } catch {
    // A malformed env value is the app's problem to surface elsewhere; do not
    // make it this module's failure.
    return configured;
  }
};

export const appConfig = {
  appName: process.env.REACT_APP_NAME,
  env: process.env.REACT_APP_ENV,
  apiBaseUrl: sameHostAsPage(process.env.REACT_APP_API_BASE_URL),
  apiBaseUrlSSR: sameHostAsPage(process.env.REACT_APP_SSR_BASE_URL),
};
